import type {
  DoctorCatalogProcedure,
  DoctorQuoteSubmissionPayload,
  DoctorQuoteWorkspaceResponse,
  DoctorQuotePreviewResponse,
  DoctorIntakeFormView,
  Quote,
  QuoteTreatmentPlanPayload,
  TreatmentItem,
} from "@jema-manager/shared";
import { apiClient, type RequestConfig } from "@shared/api/api-client";

type GetRequestConfig = Omit<RequestConfig, 'method' | 'body'>;

const basePath = "/doctors/quotes";

export const fetchDoctorQuoteWorkspace = async (
  requestId: string,
  config?: GetRequestConfig,
): Promise<DoctorQuoteWorkspaceResponse> => {
  const response = await apiClient.get<{ data: DoctorQuoteWorkspaceResponse }>(
    `${basePath}/requests/${requestId}`,
    undefined,
    config,
  );
  return response.data;
};

export const submitDoctorQuote = async (
  requestId: string,
  payload: DoctorQuoteSubmissionPayload,
): Promise<Quote> => {
  const response = await apiClient.post<{ data: Quote }>(
    `${basePath}/requests/${requestId}`,
    payload,
    {
      timeout: 45_000,
      requestLabel: "doctor-quotes:submit",
    },
  );
  return response.data;
};

export const copyGlobalCatalogProcedure = async (
  procedureId: string,
): Promise<DoctorCatalogProcedure> => {
  const response = await apiClient.post<{ data: DoctorCatalogProcedure }>(
    `/doctors/catalog/global/${encodeURIComponent(procedureId)}/copy`,
    {},
  );
  return response.data;
};

/**
 * Preview quote pricing without persisting - returns calculated fees and totals
 */
export const previewDoctorQuote = async (
  requestId: string,
  payload: DoctorQuoteSubmissionPayload,
): Promise<DoctorQuotePreviewResponse> => {
  const response = await apiClient.post<{ data: DoctorQuotePreviewResponse }>(
    `${basePath}/requests/${requestId}/preview`,
    payload,
    {
      timeout: 45_000,
      requestLabel: "doctor-quotes:preview",
    },
  );
  return response.data;
};


export const fetchDoctorQuoteIntakeForm = async (
  doctorId: string,
  quoteId: string,
  refId: string,
  config?: GetRequestConfig,
): Promise<DoctorIntakeFormView> => {
  const response = await apiClient.get<{ data: DoctorIntakeFormView }>(
    `${basePath}/${doctorId}/${quoteId}/intake/${refId}`,
    undefined,
    config,
  );
  return response.data;
};

export const fetchDoctorRequestIntakeForm = async (
  requestId: string,
  refId: string,
  config?: GetRequestConfig,
): Promise<DoctorIntakeFormView> => {
  const response = await apiClient.get<{ data: DoctorIntakeFormView }>(
    `${basePath}/requests/${requestId}/intake/${refId}`,
    undefined,
    config,
  );
  return response.data;
};

export interface EligibleRequest {
  requestId: string;
  patientName: string;
  treatmentTypes?: string[];
  createdAt: string;
  priority: string;
  availableSlots: number;
}

export interface EligibleRequestsResponse {
  data: EligibleRequest[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    hasMore: boolean;
  };
}

export interface EligibleRequestsQuery {
  page?: number;
  limit?: number;
  search?: string;
}

export const fetchEligibleRequests = async (
  query?: EligibleRequestsQuery,
  config?: GetRequestConfig,
): Promise<EligibleRequestsResponse> => {
  const params = new URLSearchParams();
  if (query?.page) params.set("page", query.page.toString());
  if (query?.limit) params.set("limit", query.limit.toString());
  if (query?.search) params.set("search", query.search);
  params.set("eligibleForQuote", "true");

  const queryString = params.toString();
  const response = await apiClient.get<EligibleRequestsResponse>(
    `/doctors/requests${queryString ? `?${queryString}` : ""}`,
    undefined,
    config,
  );
  return response;
};


/**
 * Summary information about a quote created by a doctor
 */
export interface DoctorQuoteSummary {
  id: string;
  serviceRequestId: string;
  patientId: string;
  patientName: string | null;
  referenceUid: string | null;
  treatmentTypes?: string[];
  subtotal: number;
  total: number;
  currency: string;
  status: string;
  treatmentPlan?: QuoteTreatmentPlanPayload | TreatmentItem[] | null;
  createdAt: string;
  updatedAt: string;
  submittedAt?: string | null;
}

/**
 * Response structure for doctor quotes list endpoint
 */
export interface DoctorQuotesResponse {
  data: DoctorQuoteSummary[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    hasMore: boolean;
  };
}

/**
 * Query parameters for fetching doctor quotes
 */
export interface DoctorQuotesQuery {
  page?: number;
  limit?: number;
  status?: string[];
}

/**
 * Fetch all quotes created by the authenticated doctor
 * @param query - Optional query parameters for pagination and filtering
 * @returns Paginated list of quotes with patient information
 */
export const fetchDoctorQuotes = async (
  query?: DoctorQuotesQuery,
  config?: GetRequestConfig,
): Promise<DoctorQuotesResponse> => {
  const params = new URLSearchParams();
  if (query?.page) params.set("page", query.page.toString());
  if (query?.limit) params.set("limit", query.limit.toString());
  if (query?.status) {
    query.status.forEach((s) => params.append("status[]", s));
  }

  const queryString = params.toString();
  const response = await apiClient.get<
    DoctorQuotesResponse | { data?: DoctorQuotesResponse | DoctorQuoteSummary[]; pagination?: DoctorQuotesResponse["pagination"] }
  >(
    `/doctors/me/quotes${queryString ? `?${queryString}` : ""}`,
    undefined,
    config,
  );

  if (response && typeof response === "object") {
    if (Array.isArray((response as DoctorQuotesResponse).data)) {
      return response as DoctorQuotesResponse;
    }

    const wrapped = (response as { data?: DoctorQuotesResponse }).data;
    if (wrapped && typeof wrapped === "object" && Array.isArray(wrapped.data)) {
      return wrapped;
    }
  }

  return {
    data: [],
    pagination: {
      page: query?.page ?? 1,
      limit: query?.limit ?? 20,
      total: 0,
      hasMore: false,
    },
  };
};
